import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Pause, Clock, RefreshCw } from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket';
import { useScreenSize } from '../hooks/useScreenSize';
import { usePlayerAnalytics } from '../hooks/usePlayerAnalytics';
import PDFViewer from '../components/PDFViewer';

// Widget Imports
import WeatherWidget from '../components/widgets/WeatherWidget';
import ClockWidget from '../components/widgets/ClockWidget';
import RSSWidget from '../components/widgets/RSSWidget';
import StreamingWidget from '../components/widgets/StreamingWidget';
import WebContentWidget from '../components/widgets/WebContentWidget';
import MultiZoneWidget from '../components/widgets/MultiZoneWidget';

const API_URL = (process.env.REACT_APP_API_URL || process.env.REACT_APP_API_URL);

// 🎯 Widget Registry - Zentrale Widget-Verwaltung
const WIDGET_COMPONENTS = {
  'weather-widget': WeatherWidget,
  'clock-widget': ClockWidget,
  'rss-feed-widget': RSSWidget,
  'streaming-widget': StreamingWidget,
  'web-content-widget': WebContentWidget,
  'multi-zone': MultiZoneWidget
};

// 🎯 Smart Image Component with Aspect Ratio Detection
const SmartImage = ({ src, alt }) => {
  const [objectFit, setObjectFit] = useState('cover'); // Default to cover
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    // Reset when src changes
    setImageLoaded(false);
    setObjectFit('cover');
  }, [src]);

  const handleImageLoad = (e) => {
    const img = e.target;
    const imageWidth = img.naturalWidth;
    const imageHeight = img.naturalHeight;
    const imageRatio = imageWidth / imageHeight;
    
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const screenRatio = screenWidth / screenHeight;
    
    // Calculate ratio difference (tolerance of 10%)
    const ratioDifference = Math.abs(imageRatio - screenRatio) / screenRatio;
    
    console.log('🖼️ Smart Image Analysis:', {
      image: `${imageWidth}x${imageHeight}`,
      imageRatio: imageRatio.toFixed(3),
      screen: `${screenWidth}x${screenHeight}`,
      screenRatio: screenRatio.toFixed(3),
      difference: `${(ratioDifference * 100).toFixed(1)}%`,
      decision: ratioDifference <= 0.1 ? 'contain (ratios match)' : 'cover (ratios differ)'
    });
    
    // If ratios are within 10% tolerance, use contain (no cropping needed)
    // Otherwise use cover (fill screen, slight crop acceptable)
    if (ratioDifference <= 0.1) {
      setObjectFit('contain');
    } else {
      setObjectFit('cover');
    }
    
    setImageLoaded(true);
  };

  return (
    <img 
      src={src}
      alt={alt}
      onLoad={handleImageLoad}
      className={`w-full h-full transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
      style={{ objectFit }}
    />
  );
};

// Helper function to get full media URL
const getMediaUrl = (url) => {
  if (url.startsWith('/uploads')) {
    return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch'}${url}`;
  }
  return url;
};

const PlayerPage = () => {
  // 1️⃣ Hooks
  const { uuid } = useParams();
  const screenSize = useScreenSize();
  const { trackContentViewStart, trackWidgetUsage, cleanup } = usePlayerAnalytics(uuid);
  
  // 2️⃣ State
  const [screen, setScreen] = useState(null);
  const [media, setMedia] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduledPlaylist, setScheduledPlaylist] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  // 3️⃣ Screen Info Logging
  useEffect(() => {
    console.log('📺 Player Screen Info:', {
      resolution: `${screenSize.width}x${screenSize.height}`,
      orientation: screenSize.orientation,
      category: screenSize.category,
      breakpoint: screenSize.breakpoint,
      fontMultiplier: screenSize.fontSizeMultiplier,
      spacingMultiplier: screenSize.spacingMultiplier
    });
  }, [screenSize]);

  // 4️⃣ Callbacks
  const checkSchedule = useCallback(async (screenId) => {
    if (!screenId) return null;

    try {
      const response = await fetch(`${API_URL}/schedules/active/${screenId}`);
      const data = await response.json();
      
      if (data && data.playlist_id) {
        console.log('📅 Active schedule found, playlist:', data.playlist_id);
        setScheduledPlaylist(data.playlist_id);
        return data.playlist_id;
      } else {
        console.log('📅 No active schedule');
        setScheduledPlaylist(null);
        return null;
      }
    } catch (error) {
      console.error('Error checking schedule:', error);
      return null;
    }
  }, []);

  const loadPlaylistMedia = useCallback(async (playlistId) => {
    try {
      console.log('📂 Loading playlist:', playlistId);
      
      const playlistRes = await fetch(`${API_URL}/playlists/public/${playlistId}`);
      
      if (!playlistRes.ok) {
        throw new Error('Playlist not found');
      }
      
      const playlistData = await playlistRes.json();
      
      console.log('📋 Playlist data:', playlistData);
      
      if (playlistData.media_items && playlistData.media_items.length > 0) {
        setMedia(playlistData.media_items);
        setLastUpdate(new Date());
        console.log(`✅ Loaded ${playlistData.media_items.length} items (media + widgets)`);
        return true;
      } else {
        console.log('⚠️ Playlist is empty');
        setMedia([]);
        return false;
      }
    } catch (error) {
      console.error('❌ Error loading playlist:', error);
      return false;
    }
  }, []);

  const loadScreenData = useCallback(async () => {
    try {
      console.log('🔄 Loading screen data for UUID:', uuid);
      
      const screenRes = await fetch(`${API_URL}/screens/uuid/${uuid}`);
      
      if (!screenRes.ok) {
        throw new Error('Screen not found');
      }
      
      const screenData = await screenRes.json();
      setScreen(screenData);
      console.log('✅ Screen loaded:', screenData.name);

      const activeScheduledPlaylist = await checkSchedule(screenData.id);

      if (activeScheduledPlaylist) {
        console.log('✅ Using scheduled playlist:', activeScheduledPlaylist);
        await loadPlaylistMedia(activeScheduledPlaylist);
      } else if (screenData.playlist_id) {
        console.log('✅ Using assigned playlist:', screenData.playlist_id);
        await loadPlaylistMedia(screenData.playlist_id);
      } else {
        console.log('📂 No playlist assigned, loading all media');
        const mediaRes = await fetch(`${API_URL}/media/public/${screenData.organization_id}`);
        const mediaData = await mediaRes.json();
        setMedia(mediaData);
        setLastUpdate(new Date());
      }

      await fetch(`${API_URL}/screens/uuid/${uuid}/heartbeat`, { 
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'online' })
      });
    } catch (err) {
      console.error('❌ Error loading:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [uuid, checkSchedule, loadPlaylistMedia]);

  // Separate function to reload only playlist content
  const reloadCurrentPlaylist = useCallback(async () => {
    try {
      console.log('🔄 Reloading current playlist content...');
      
      const screenRes = await fetch(`${API_URL}/screens/uuid/${uuid}`);
      
      if (!screenRes.ok) {
        throw new Error('Failed to fetch screen data');
      }
      
      const freshScreenData = await screenRes.json();
      console.log('🆕 Fresh screen data:', {
        name: freshScreenData.name,
        playlist_id: freshScreenData.playlist_id
      });
      
      setScreen(freshScreenData);
      
      const activeSchedule = await checkSchedule(freshScreenData.id);
      
      let playlistToReload = null;
      
      if (activeSchedule) {
        console.log('📅 Using scheduled playlist:', activeSchedule);
        playlistToReload = activeSchedule;
        setScheduledPlaylist(activeSchedule);
      } else if (freshScreenData.playlist_id) {
        console.log('📋 Using assigned playlist:', freshScreenData.playlist_id);
        playlistToReload = freshScreenData.playlist_id;
        setScheduledPlaylist(null);
      } else {
        console.log('📂 No playlist, loading all media');
        const mediaRes = await fetch(`${API_URL}/media/public/${freshScreenData.organization_id}`);
        const mediaData = await mediaRes.json();
        setMedia(mediaData);
        setCurrentIndex(0);
        setLastUpdate(new Date());
        return;
      }
      
      if (playlistToReload) {
        const success = await loadPlaylistMedia(playlistToReload);
        if (success) {
          console.log('✅ Playlist reloaded');
          setCurrentIndex(0);
        }
      }
    } catch (error) {
      console.error('❌ Error reloading:', error);
      loadScreenData();
    }
  }, [uuid, checkSchedule, loadPlaylistMedia, loadScreenData]);

  // WebSocket Handler
  const handleWebSocketMessage = useCallback((message) => {
    console.log('📨 Player received:', message.type, message.data);

    switch (message.type) {
      case 'playlist_changed':
        console.log('🔄 Playlist changed, reloading content...');
        reloadCurrentPlaylist();
        break;
        
      case 'media_updated':
        console.log('🔄 Media updated, reloading...');
        reloadCurrentPlaylist();
        break;

      case 'schedule_created':
      case 'schedule_updated':
      case 'schedule_deleted':
        console.log('🔄 Schedule changed, checking...');
        if (screen?.id) {
          checkSchedule(screen.id).then((newScheduledPlaylist) => {
            if (newScheduledPlaylist !== scheduledPlaylist) {
              console.log('🔄 Reloading due to schedule change');
              loadScreenData();
            }
          });
        }
        break;
        
      default:
        console.log('ℹ️ Unhandled message type:', message.type);
        break;
    }
  }, [screen?.id, scheduledPlaylist, checkSchedule, reloadCurrentPlaylist, loadScreenData]);

  // 5️⃣ WebSocket
  useWebSocket('player', uuid, handleWebSocketMessage);

  // 6️⃣ Effects
  useEffect(() => {
    if (uuid) {
      loadScreenData();
    }
  }, [uuid, loadScreenData]);

  // Auto-Advance Timer mit Content View Tracking
  useEffect(() => {
    if (!isPlaying || media.length === 0) return;

    const currentMedia = media[currentIndex];

    trackContentViewStart(currentMedia);
    
    if (currentMedia?.type === 'video') {
      console.log('🎬 Video playing, waiting for onEnded event');
      return;
    }

    const duration = (currentMedia?.duration || 10) * 1000;
    
    console.log(`⏱️ Setting timer for ${duration}ms`, {
      type: currentMedia?.type || 'widget',
      name: currentMedia?.name,
      feature_id: currentMedia?.feature_id,
      currentIndex,
      nextIndex: (currentIndex + 1) % media.length
    });
    
    const timer = setTimeout(() => {
      console.log('⏰ Timer expired, advancing to next item');
      setCurrentIndex((prev) => {
        const next = (prev + 1) % media.length;
        console.log(`➡️ Moving from index ${prev} to ${next}`);
        return next;
      });
    }, duration);

    return () => {
      console.log('🧹 Cleaning up timer');
      clearTimeout(timer);
    };
  }, [currentIndex, media, isPlaying, trackContentViewStart]);

  // Widget Usage Tracking
  useEffect(() => {
    if (!isPlaying || media.length === 0) return;

    const currentMedia = media[currentIndex];
    
    if (currentMedia?.item_type === 'widget' || currentMedia?.type === 'widget' || currentMedia?.feature_id) {
      trackWidgetUsage(currentMedia, 'viewed', {
        duration: currentMedia.duration || 10
      });
    }
  }, [isPlaying, currentIndex, media, trackWidgetUsage]);

  // Heartbeat Effect
  useEffect(() => {
    if (!uuid) return;

    const interval = setInterval(async () => {
      try {
        await fetch(`${API_URL}/screens/uuid/${uuid}/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status: 'online' })
        });
      } catch (error) {
        console.error('❌ Heartbeat failed:', error);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [uuid]);

  // Cleanup beim Unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // 7️⃣ Loading/Error States
  if (loading) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-xl">Loading player...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-red-900 to-orange-900">
        <div className="text-center text-white">
          <p className="text-2xl mb-4">⚠️ Error</p>
          <p className="text-lg">{error}</p>
        </div>
      </div>
    );
  }

  if (media.length === 0) {
    return (
      <div className="w-screen h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-700">
        <div className="text-center text-white">
          <p className="text-2xl mb-4">📺 No Content</p>
          <p className="text-lg">Please add media or widgets to your playlist</p>
        </div>
      </div>
    );
  }

  const currentMedia = media[currentIndex];

  // Helper function to get full media URL
  const getMediaUrl = (url) => {
    if (url.startsWith('/uploads')) {
      return `${process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://app.alpenscreen.ch'}${url}`;
    }
    return url;
  };

  // 8️⃣ Render
  return (
    <div className="w-screen h-screen bg-black overflow-hidden relative">
      {/* Debug Info Overlay */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute top-4 right-4 z-50 bg-black/80 text-white text-xs p-3 rounded-lg font-mono max-w-xs">
          <div className="flex items-center gap-2 mb-2 text-green-400">
            <RefreshCw size={12} />
            <span>Live Updates Active</span>
          </div>
          <div className="border-b border-white/20 mb-2 pb-2">
            <div>Resolution: {screenSize.width}x{screenSize.height}</div>
            <div>Category: {screenSize.category}</div>
            <div>Breakpoint: {screenSize.breakpoint}</div>
            <div>Orientation: {screenSize.orientation}</div>
            <div>Font Scale: {screenSize.fontSizeMultiplier}x</div>
          </div>
          <div className="border-b border-white/20 mb-2 pb-2">
            <div>Current: {currentIndex + 1}/{media.length}</div>
            <div>Type: {currentMedia?.type || 'widget'}</div>
            <div>Duration: {currentMedia?.duration || 10}s</div>
            {currentMedia?.feature_id && (
              <div>Widget: {currentMedia.feature_id}</div>
            )}
          </div>
          {lastUpdate && (
            <div className="text-green-400 text-[10px]">
              Last update: {lastUpdate.toLocaleTimeString()}
            </div>
          )}
        </div>
      )}

      {/* Playback Controls */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-40 bg-black/50 backdrop-blur-md rounded-full px-6 py-3 flex items-center gap-4 opacity-0 hover:opacity-100 transition-opacity">
        <button
          onClick={() => setIsPlaying(!isPlaying)}
          className="text-white hover:text-blue-400 transition-colors"
        >
          {isPlaying ? <Pause size={24} /> : <Play size={24} />}
        </button>
        <div className="text-white text-sm">
          {currentIndex + 1} / {media.length}
        </div>
        <div className="text-white/70 text-xs flex items-center gap-2">
          <Clock size={16} />
          {currentMedia?.duration || 10}s
        </div>
      </div>

      {/* Content Display */}
      <div className="w-full h-full flex items-center justify-center">
        {currentMedia?.feature_id ? (
          // 🎯 Widget Rendering - Dynamic Registry
          <div className="w-full h-full">
            {(() => {
              const WidgetComponent = WIDGET_COMPONENTS[currentMedia.feature_id];
              
              if (WidgetComponent) {
                console.log('🎨 Rendering widget:', currentMedia.feature_id);
                
                const widgetProps = {
                  config: currentMedia.config,
                  screenSize: screenSize
                };
                
                if (currentMedia.feature_id === 'multi-zone') {
                  widgetProps.organizationId = screen?.organization_id;
                  console.log('🔧 MultiZone: Adding organizationId:', screen?.organization_id);
                }
                
                return <WidgetComponent {...widgetProps} />;
              }
              
              // Unknown Widget Fallback
              return (
                <div className="w-full h-full flex items-center justify-center bg-gray-900 text-white">
                  <div className="text-center">
                    <p className="text-2xl mb-4">⚠️ Unknown Widget</p>
                    <p className="text-lg">Feature ID: {currentMedia.feature_id}</p>
                    <p className="text-sm mt-4 opacity-70">
                      Available Widgets: {Object.keys(WIDGET_COMPONENTS).join(', ')}
                    </p>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          // Media Rendering
          <>
            {currentMedia?.type === 'image' && (
              <SmartImage 
                src={getMediaUrl(currentMedia.url)}
                alt={currentMedia.name}
              />
            )}
            
            {currentMedia?.type === 'video' && (
              <video 
                key={currentMedia.id}
                autoPlay 
                muted 
                className="w-full h-full object-cover"
                onEnded={() => {
                  console.log('🎬 Video ended, advancing to next item');
                  setCurrentIndex((prev) => (prev + 1) % media.length);
                }}
              >
                <source 
                  src={getMediaUrl(currentMedia.url)}
                  type="video/mp4" 
                />
              </video>
            )}
            
            {currentMedia?.type === 'webpage' && (
              <iframe 
                src={currentMedia.url} 
                className="w-full h-full border-0"
                title={currentMedia.name}
              />
            )}
            
            {currentMedia?.type === 'pdf' && (
              <PDFViewer
                url={getMediaUrl(currentMedia.url)}
                className="w-full h-full"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PlayerPage;